import { DAY_MS, muscleRegion, type MuscleRegion } from '@jackedlogs/shared';

// Auto-suggested workout name (Antenore 2026-06-23): "<Weekday> W<isoWeek> ·
// <Region1>+<Region2>", e.g. "Monday W26 · Lower body+Back". The weekday +
// ISO week locate the session in the training week; the two most-worked
// muscle REGIONS (by volume) say what it was. It's a LIVE suggestion: an
// empty workout is just "<Weekday> W<isoWeek>", the region suffix grows as
// exercises are logged. Never persisted as a snapshot — it's the fallback
// the save path applies only when the user left the name blank
// ([[feedback_no_denormalized_display_snapshots]]).

// ISO-8601 week number (the week belongs to the year of its Thursday). Uses
// LOCAL date components so the weekday/week match what the user sees, then
// normalises in UTC to avoid DST drift in the day arithmetic.
export function isoWeekNumber(date: Date): number {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  const dayNum = d.getUTCDay() || 7; // Mon=1..Sun=7
  d.setUTCDate(d.getUTCDate() + 4 - dayNum); // shift to this week's Thursday
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  return Math.ceil(((d.getTime() - yearStart.getTime()) / DAY_MS + 1) / 7);
}

export type NameSuggestInput = {
  startedAt: string;
  // workoutTotals(...).byMuscle — per-muscle volume, any order.
  byMuscle: ReadonlyArray<{ muscle: string; volumeKg: number }>;
  locale: string;
  // Localised region label, e.g. (r) => t(`catalog.region_${r}`).
  regionLabel: (region: MuscleRegion) => string;
};

export function suggestWorkoutName(input: NameSuggestInput): string {
  const d = new Date(input.startedAt);
  if (Number.isNaN(d.getTime())) return '';

  const weekday = new Intl.DateTimeFormat(input.locale, {
    weekday: 'long',
  }).format(d);
  const weekdayCap = weekday.charAt(0).toUpperCase() + weekday.slice(1);
  const prefix = `${weekdayCap} W${isoWeekNumber(d)}`;

  // Aggregate volume per REGION (multiple muscles collapse into one group),
  // then take the top two by volume. Cardio is excluded — it's not a
  // strength region and would crowd out the muscles that name the session.
  const byRegion = new Map<MuscleRegion, number>();
  for (const { muscle, volumeKg } of input.byMuscle) {
    if (!(volumeKg > 0)) continue;
    const r = muscleRegion(muscle);
    if (!r || r === 'cardio') continue;
    byRegion.set(r, (byRegion.get(r) ?? 0) + volumeKg);
  }
  const topRegions = [...byRegion.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([r]) => input.regionLabel(r));

  return topRegions.length ? `${prefix} · ${topRegions.join('+')}` : prefix;
}
